import { useMemo } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import { useQuery } from '@tanstack/react-query';

import type { Tier } from '~/interfaces/subscriptions';
import type { loader as communityLoader } from '~/routes/community';

export interface CommunityPurchasableTiers {
  purchasableTiers: Tier[] | null;
  isError: boolean;
  isPending: boolean;
  /** Optimistic while loading: only a resolved empty set (or a failed fetch) reports false. */
  hasPurchasableTier: boolean;
}

interface PricingDetailsResponse {
  tiers: Tier[];
}

export class PricingDetailsFetchError extends Error {
  status: number;

  constructor(status: number) {
    super(`Failed to fetch pricing details (${status})`);
    this.status = status;
  }
}

export const fetchPricingDetails = async (): Promise<PricingDetailsResponse> => {
  const response = await fetch('/pricing_details', { headers: { 'Sec-Fetch-Dest': 'empty' } });
  if (!response.ok) throw new PricingDetailsFetchError(response.status);
  return response.json();
};

/**
 * Shared catalog fetch (query key: `['community-pricing-details']`) narrowed to
 * whichever `gatingTierIds` the caller resolves — community-wide or space-scoped.
 * `purchasableTiers` stays null while loading; an empty array means nothing in
 * `gatingTierIds` is actually sellable (e.g. every linked tier was archived on
 * Stripe). Shared by `useCommunityPurchasableTiers` and `useSpacePurchasableTiers`
 * so both read from the same cached fetch instead of duplicating it.
 *
 * Owns its query instead of sharing usePricingDetails: the endpoint answers 401
 * when the publication can't sell premium at all (charges disabled, premium off,
 * Stripe disconnected), and that must be terminal — retrying it forever kept the
 * modal from ever settling on the "no plans available" state.
 */
export const usePurchasableTiersForIds = (
  gatingTierIds: string[] | undefined,
  enabled: boolean,
): CommunityPurchasableTiers => {
  const { data, isError, isPending } = useQuery({
    queryKey: ['community-pricing-details'],
    queryFn: fetchPricingDetails,
    enabled,
    staleTime: 30_000,
    // A data-less errored query resets to pending when any refetch starts, so an
    // observer mount or window focus would flap consumers gating on isPending.
    retryOnMount: false,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      !(error instanceof PricingDetailsFetchError && error.status === 401) && failureCount < 1,
  });

  const purchasableTiers = useMemo(() => {
    if (!data) return null;

    return data.tiers.filter((tier) => gatingTierIds?.includes(tier.id));
  }, [data, gatingTierIds]);

  const hasPurchasableTier = !isError && (purchasableTiers === null || purchasableTiers.length > 0);

  return { purchasableTiers, isError, isPending, hasPurchasableTier };
};

/**
 * Fetches the pricing data and narrows it to the community's gating tiers.
 * `purchasableTiers` stays null while loading; an empty array means nothing is
 * actually sellable (e.g. every linked tier was archived on Stripe).
 */
export const useCommunityPurchasableTiers = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): CommunityPurchasableTiers => {
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const communityTierIds = layoutData?.community?.premium_tier_ids;

  return usePurchasableTiersForIds(communityTierIds, enabled);
};
